import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  ReloadOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  DatePicker,
  Divider,
  Form,
  Row,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { FC, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IPreparingTimeAreaStat,
  IPreparingTimeStats,
} from "../../../models/statistcs/respons";
import Table from "../../general/table-components/table";
import styles from "./style.module.scss";

interface IProps {
  stats?: IPreparingTimeStats;
  onChange: (stats: IPreparingTimeStats) => void;
  hideDateControls?: boolean;
}

interface IPreparingTimeAreaRow extends IPreparingTimeAreaStat {
  key: string;
  isOutside: boolean;
}

type FieldErrors = Partial<Record<"startDate" | "endDate", string>>;

const DATE_FORMAT = "YYYY-MM-DD";

const extractFieldError = (error: any, field: "startDate" | "endDate") => {
  const errors = error?.response?.data?.errors;
  const value = errors?.[field];

  if (Array.isArray(value)) return value.filter(Boolean).join(", ");
  if (typeof value === "string") return value;

  return undefined;
};

const getNullableNumber = (value: number | null | undefined) =>
  typeof value === "number" && Number.isFinite(value) ? value : null;

const normalizeDatePickerValue = (value: string | string[]) =>
  Array.isArray(value) ? value[0] : value || undefined;

const PreparingTimeStats: FC<IProps> = ({
  stats,
  onChange,
  hideDateControls,
}) => {
  const { t, i18n } = useTranslation();
  const [startDate, setStartDate] = useState<string | undefined>(
    stats?.startDate
  );
  const [endDate, setEndDate] = useState<string | undefined>(stats?.endDate);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  const numberFormatter = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language]
  );

  useEffect(() => {
    setStartDate(stats?.startDate);
    setEndDate(stats?.endDate);
  }, [stats?.endDate, stats?.startDate]);

  const areaRows = useMemo<IPreparingTimeAreaRow[]>(() => {
    if (!stats) return [];

    const buildRow = (
      item: IPreparingTimeAreaStat,
      index: number,
      isOutside = false
    ): IPreparingTimeAreaRow => ({
      ...item,
      key: isOutside ? "outside-areas" : `area-${item.areaId ?? index}`,
      isOutside,
    });

    return [
      ...(stats.areas || []).map((area, index) => buildRow(area, index)),
      ...(stats.outsideAreas
        ? [buildRow(stats.outsideAreas, -1, true)]
        : []),
    ];
  }, [stats]);

  if (!stats) return null;

  const disabledFutureDate = (date: Dayjs) =>
    date && date.isAfter(dayjs(), "day");

  const formatNumber = (value: number | null | undefined) => {
    const number = getNullableNumber(value);
    return number === null ? "-" : numberFormatter.format(number);
  };

  const formatMinutes = (value: number | null | undefined) => {
    const number = getNullableNumber(value);
    return number === null
      ? "-"
      : `${numberFormatter.format(number)} ${t("minutes")}`;
  };

  const formatPercent = (value: number | null | undefined) => {
    const number = getNullableNumber(value);
    return number === null ? "-" : `${numberFormatter.format(number)}%`;
  };

  const validateDates = () => {
    const errors: FieldErrors = {};
    const today = dayjs();
    const start = startDate ? dayjs(startDate, DATE_FORMAT, true) : null;
    const end = endDate ? dayjs(endDate, DATE_FORMAT, true) : null;

    if (startDate && (!start?.isValid() || start.isAfter(today, "day"))) {
      errors.startDate = t("invalid_date");
    }

    if (endDate && (!end?.isValid() || end.isAfter(today, "day"))) {
      errors.endDate = t("invalid_date");
    }

    if (start?.isValid() && end?.isValid() && end.isBefore(start, "day")) {
      errors.endDate = t("end_date_before_start_date");
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const refreshPreparingTimeStats = async () => {
    if (!validateDates()) return;

    try {
      setLoading(true);
      setFieldErrors({});

      return;
    } catch (error) {
      setFieldErrors({
        startDate: extractFieldError(error, "startDate"),
        endDate: extractFieldError(error, "endDate"),
      });
    } finally {
      setLoading(false);
    }
  };

  const areaColumns: ColumnsType<IPreparingTimeAreaRow> = [
    {
      title: t("area"),
      dataIndex: "areaName",
      key: "areaName",
      ellipsis: true,
      render: (name: string, row) => (
        <Tooltip title={row.isOutside ? t("outside_areas") : name}>
          <div className={styles.areaName}>
            <span>{row.isOutside ? t("outside_areas") : name}</span>
            {row.isOutside && <Tag color="blue">{t("outside_areas")}</Tag>}
          </div>
        </Tooltip>
      ),
    },
    {
      title: t("orders"),
      dataIndex: "orderCount",
      key: "orderCount",
      width: 100,
      sorter: (a, b) => a.orderCount - b.orderCount,
      render: (value: number) => (
        <span className={styles.count}>{numberFormatter.format(value)}</span>
      ),
    },
    {
      title: t("average"),
      dataIndex: "averagePreparingMinutes",
      key: "averagePreparingMinutes",
      width: 130,
      sorter: (a, b) =>
        (a.averagePreparingMinutes ?? -1) -
        (b.averagePreparingMinutes ?? -1),
      render: formatMinutes,
    },
    {
      title: t("median"),
      dataIndex: "medianPreparingMinutes",
      key: "medianPreparingMinutes",
      width: 130,
      sorter: (a, b) =>
        (a.medianPreparingMinutes ?? -1) - (b.medianPreparingMinutes ?? -1),
      render: formatMinutes,
    },
    {
      title: t("p90"),
      dataIndex: "p90PreparingMinutes",
      key: "p90PreparingMinutes",
      width: 130,
      sorter: (a, b) =>
        (a.p90PreparingMinutes ?? -1) - (b.p90PreparingMinutes ?? -1),
      render: formatMinutes,
    },
    {
      title: t("customer_accepted_rate"),
      dataIndex: "customerAcceptedRate",
      key: "customerAcceptedRate",
      width: 150,
      render: formatPercent,
    },
    {
      title: t("unavailable_product_order_rate"),
      dataIndex: "unavailableProductOrderRate",
      key: "unavailableProductOrderRate",
      width: 170,
      render: formatPercent,
    },
  ];

  return (
    <div className={styles.container}>
      <Divider orientation="center">{t("preparing_time_stats")}</Divider>
      <Card className={styles.panel} bodyStyle={{ padding: 16 }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} lg={hideDateControls ? 24 : 8}>
            <Statistic
              title={
                <Space>
                  <ClockCircleOutlined />
                  <span>{t("average_preparing_time")}</span>
                </Space>
              }
              value={formatNumber(stats.averagePreparingMinutes)}
              suffix={
                getNullableNumber(stats.averagePreparingMinutes) === null
                  ? undefined
                  : t("minutes")
              }
            />
            <Typography.Text type="secondary">
              {numberFormatter.format(stats.includedOrderCount)}{" "}
              {t("completed_orders")}
            </Typography.Text>
            <div className={styles.period}>
              {stats.startDate} - {stats.endDate}
              {stats.excludedOrderCount > 0 && (
                <Tooltip
                  title={t("excluded_preparing_time_orders_tooltip", {
                    count: stats.excludedOrderCount,
                  })}
                >
                  <Tag color="warning" icon={<WarningOutlined />}>
                    {numberFormatter.format(stats.excludedOrderCount)}
                  </Tag>
                </Tooltip>
              )}
            </div>
          </Col>
          {!hideDateControls && (
            <Col xs={24} lg={16}>
              <Row gutter={[12, 8]} align="top">
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={t("startDate")}
                    validateStatus={fieldErrors.startDate ? "error" : undefined}
                    help={fieldErrors.startDate}
                  >
                    <DatePicker
                      allowClear
                      disabledDate={disabledFutureDate}
                      format={DATE_FORMAT}
                      value={startDate ? dayjs(startDate) : undefined}
                      onChange={(_, value) =>
                        setStartDate(normalizeDatePickerValue(value))
                      }
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={8}>
                  <Form.Item
                    label={t("endDate")}
                    validateStatus={fieldErrors.endDate ? "error" : undefined}
                    help={fieldErrors.endDate}
                  >
                    <DatePicker
                      allowClear
                      disabledDate={disabledFutureDate}
                      format={DATE_FORMAT}
                      value={endDate ? dayjs(endDate) : undefined}
                      onChange={(_, value) =>
                        setEndDate(normalizeDatePickerValue(value))
                      }
                      style={{ width: "100%" }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Button
                type="primary"
                icon={<ReloadOutlined />}
                loading={loading}
                onClick={refreshPreparingTimeStats}
              >
                {t("refresh")}
              </Button>
            </Col>
          )}
        </Row>

        <Row gutter={[12, 12]} className={styles.metrics}>
          <Col xs={24} sm={12} lg={8} xl={4}>
            <Statistic
              title={t("p90_preparing_time")}
              value={formatNumber(stats.p90PreparingMinutes)}
              suffix={
                getNullableNumber(stats.p90PreparingMinutes) === null
                  ? undefined
                  : t("minutes")
              }
            />
          </Col>
          <Col xs={24} sm={12} lg={8} xl={5}>
            <Statistic
              title={t("direct_to_driver_orders")}
              value={formatNumber(stats.directToDriverCount)}
              suffix={formatPercent(stats.directToDriverRate)}
              prefix={<CheckCircleOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} lg={8} xl={5}>
            <Statistic
              title={t("customer_accepted_orders")}
              value={formatNumber(stats.customerAcceptedCount)}
              suffix={formatPercent(stats.customerAcceptedRate)}
              prefix={<WarningOutlined />}
            />
          </Col>
          <Col xs={24} sm={12} lg={8} xl={5}>
            <Statistic
              title={t("unavailable_product_orders")}
              value={formatNumber(stats.unavailableProductOrderCount)}
              suffix={formatPercent(stats.unavailableProductOrderRate)}
            />
          </Col>
          <Col xs={24} sm={12} lg={8} xl={3}>
            <Statistic
              title={t("unavailable_products")}
              value={formatNumber(stats.unavailableProductCount)}
            />
          </Col>
        </Row>

        <div className={styles.tabs}>
          <Table
            rowKey="key"
            columns={areaColumns}
            dataSource={areaRows}
            pagination={false}
            rowClassName={(row) => (row.isOutside ? styles.outsideRow : "")}
            scroll={{ x: 980 }}
          />
        </div>
      </Card>
    </div>
  );
};

export default PreparingTimeStats;
